import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    PasswordField,
    SelectField,
    TextField,
} from '@speeki/global-ui-components';

import { StandardForm } from '@components/atoms/StandardForm/StandardForm';
import { TooltipWithQuestionMark } from '@components/molecules/TooltipWithQuestionMark/TooltipWithQuestionMark';

import { countriesOptions, relationshipOptions } from '@utils/dictionary';
import { useQueryParam } from '@utils/hooks/useQueryParam';

import { SignUpFormValues } from '@domains/auth/pages/SignUpPage/SignUpPage';
import { passwordRequirements } from '@shared/passwordRequirments';

import { PasswordCheck } from './SignUpForm.styled';

export const SignUpForm = () => {
    const { t } = useTranslation();

    const userEmail = useQueryParam('userEmail');

    const {
        control,
        formState: { dirtyFields, errors, isSubmitted, isValid },
        register,
        watch,
    } = useFormContext<SignUpFormValues>();

    const password = watch('password');
    const isPasswordCheckOpened =
        dirtyFields.password || (!isValid && isSubmitted);

    const { field: countrySelect } = useController({
        control,
        name: 'country',
    });

    const { field: relationshipSelect } = useController({
        control,
        name: 'relationship',
    });

    return (
        <StandardForm>
            <TextField
                {...register('email')}
                className="htmlToImageIgnore"
                defaultValue={userEmail ?? ''}
                error={!!errors.email}
                label={
                    <>
                        {t('EMAIL')}
                        <TooltipWithQuestionMark
                            placement={'top-start'}
                            testId="z18zdvJ"
                            text={t('SIGN_UP_TOOLTIP_TEXT')}
                        />
                    </>
                }
                message={t(errors.email?.message || '')}
                placeholder={t('REGISTER_EMAIL_PLACEHOLDER')}
                testId="fNd11Sk"
                type={'email'}
            />
            <div>
                <PasswordField
                    {...register('password')}
                    className="htmlToImageIgnore"
                    error={!!errors.password}
                    label={t('PASSWORD')}
                    name={'password'}
                    placeholder={t('PASSWORD_PLACEHOLDER')}
                    testId="XIGZWP2"
                />
                <PasswordCheck
                    isDirty={isPasswordCheckOpened}
                    isSubmitted={isSubmitted}
                    passwordValue={password}
                    requirements={passwordRequirements}
                    testId="os9vfr1"
                />
            </div>
            <PasswordField
                {...register('rePassword')}
                className="htmlToImageIgnore"
                error={!!errors.rePassword}
                label={t('CONFIRM_PASSWORD')}
                message={t(errors.rePassword?.message || '')}
                name={'rePassword'}
                placeholder={t('CONFIRM_PASSWORD_PLACEHOLDER')}
                testId="hcrX7AK"
            />
            <SelectField
                {...relationshipSelect}
                error={!!errors.relationship}
                label={'Relationship to the entity'}
                message={t(errors.relationship?.value?.message || '')}
                options={relationshipOptions()}
                placeholder={t('RELATIONSHIP_TO_COMPANY_PLACEHOLDER')}
                testId="select_sign-up-relationship"
            />
            <SelectField
                {...countrySelect}
                error={!!errors.country}
                label={t('COUNTRY')}
                message={t(errors.country?.value?.message || '')}
                options={countriesOptions()}
                placeholder={t('SELECT_COUNTRY')}
                testId="select_sign-up-country"
            />
        </StandardForm>
    );
};
